/**
 * Get color for a sentiment type
 */
export const getSentimentColor = (sentiment: string): string => {
  switch (sentiment.toLowerCase()) {
    case 'very positive':
      return 'bg-green-700';
    case 'positive':
      return 'bg-green-500';
    case 'neutral':
      return 'bg-gray-400';
    case 'negative':
      return 'bg-red-500';
    case 'very negative':
      return 'bg-red-700';
    default:
      // Generate a consistent random color for custom sentiments
      return `bg-blue-${400 + (sentiment.length * 100) % 500}`;
  }
};

/**
 * Get CSS color value for a sentiment
 */
export const getSentimentColorValue = (sentiment: string): string => {
  switch (sentiment.toLowerCase()) {
    case 'very positive':
      return '#15803d'; // green-700
    case 'positive':
      return '#22c55e'; // green-500
    case 'neutral':
      return '#9ca3af'; // gray-400
    case 'negative':
      return '#ef4444'; // red-500
    case 'very negative':
      return '#b91c1c'; // red-700
    default:
      // Default for unknown sentiment types
      return '#3b82f6'; // blue-500
  }
};

/**
 * Get Y-axis labels for the activity chart
 */
export const getYAxisLabels = (maxValue: number): number[] => {
  // Create 5 evenly spaced labels from 0 to maxValue
  const step = Math.ceil(maxValue / 4);
  return [0, step, step * 2, step * 3, maxValue];
};

/**
 * Get color for a category
 */
export const getCategoryColor = (category: string, index: number): string => {
  // Predefined colors for common categories
  const categoryColors: Record<string, string> = {
    // Standard Categories
    'news': '#3b82f6', // blue-500
    'press release': '#8b5cf6', // violet-500
    'blog': '#ec4899', // pink-500 (Matches 'Commercial' legend)
    'social media': '#f97316', // orange-500 (Matches 'Charity' legend)
    'product review': '#10b981', // emerald-500 (Matches 'N/A' legend)
    'interview': '#a855f7', // purple-500 (Matches 'Sponsorship' legend)
    'feature article': '#3b82f6', // blue-500 (Matches 'Unmentioned' legend)
    'opinion': '#f43f5e', // rose-500
    
    // Legend Specific Categories (ensure consistency)
    'commercial': '#ec4899', // pink-500
    'sponsorship': '#a855f7', // purple-500 
    'charity': '#f97316', // orange-500
    'unmentioned': '#3b82f6', // blue-500
    'n/a': '#10b981', // emerald-500
    'uncategorized': '#10b981', // emerald-500 (Treat same as N/A)
    
    // Potential other categories if needed
    'sponsorships/charities': '#ec4899', // Defaulting to pink for now
    'print': '#3b82f6', // blue-500
    'online': '#8b5cf6', // violet-500
    'tv': '#10b981', // emerald-500
    'radio': '#f97316', // orange-500
  };
  
  // Use predefined color if available
  const lowerCategory = category.toLowerCase();
  if (categoryColors[lowerCategory]) {
    return categoryColors[lowerCategory];
  }
  
  // Generate colors based on index for other categories
  const colors = [
    '#3b82f6', // blue-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#f97316', // orange-500
    '#10b981', // emerald-500
    '#a855f7', // purple-500
    '#f43f5e', // rose-500
    '#fbbf24', // amber-500
    '#6366f1', // indigo-500
    '#14b8a6', // teal-500
  ];
  
  return colors[index % colors.length];
};

/**
 * Calculates luminance of a hex color
 */
const getLuminance = (hex: string): number => {
  let r = 0, g = 0, b = 0;
  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  // 6 digits
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }
  // Normalize RGB values to 0-1 range
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;

  // Apply gamma correction
  const rG = rN <= 0.03928 ? rN / 12.92 : Math.pow((rN + 0.055) / 1.055, 2.4);
  const gG = gN <= 0.03928 ? gN / 12.92 : Math.pow((gN + 0.055) / 1.055, 2.4);
  const bG = bN <= 0.03928 ? bN / 12.92 : Math.pow((bN + 0.055) / 1.055, 2.4);
  
  // Calculate luminance
  return 0.2126 * rG + 0.7152 * gG + 0.0722 * bG;
};

/**
 * Determines the best contrasting text color (black or white) for a given background color.
 */
const getContrastingTextColor = (backgroundColorHex: string): string => {
  const luminance = getLuminance(backgroundColorHex);
  // Use white text for dark backgrounds (luminance < 0.5) and black text for light backgrounds
  return luminance < 0.5 ? '#ffffff' : '#1f2937'; // white or gray-800
};

/**
 * Get background and text colors for a tag based on type and value.
 * Uses colors defined for charts.
 */
export const getTagColors = (
  type: 'sentiment' | 'category', 
  value: string, 
  index: number = 0 // Used for category fallback colors
): { backgroundColor: string; textColor: string } => {
  let backgroundColor = '#9ca3af'; // Default gray-400

  if (type === 'sentiment') {
    backgroundColor = getSentimentColorValue(value);
  } else if (type === 'category') {
    backgroundColor = getCategoryColor(value, index);
  }
  
  const textColor = getContrastingTextColor(backgroundColor);

  return { backgroundColor, textColor };
}; 